import React, {useEffect, useMemo, useRef, useState} from 'react'
import {useLocation, useNavigate, useParams} from "react-router-dom";
import '../assets/css/main.css'
import "bootstrap/dist/css/bootstrap.min.css";
import Header from "../ThemeComponents/Header";
import RedBackground from "../ThemeComponents/RedBackground";
import VehicleComponent from "../ThemeComponents/LocationPage/VehicleComponent";
import RentTypeHeader from "../ThemeComponents/LocationPage/RentTypeHeader";
import Footer from "../ThemeComponents/Footer";
import {getLocationInfo} from "../network/publicCalls/GETLocationInfo";
import LocationPageLoader from "../ThemeComponents/LocationPage/LocationPageLoader";
import Filter from "../ThemeComponents/LocationPage/Filter";
import LocationSchedule from "../ThemeComponents/LocationPage/LocationSchedule";
import LocationSeo from "../ThemeComponents/LocationPage/LocationSeo";
import {searchVehiclesAPI} from "../network/publicCalls/GETSearchVehicles";
import {collapseEquivalentVehicles} from "../config/locationAvailability";
import {DISABLED_CACHE} from "../config/network";
import FakeLocationPage from "./FakeLocationPage";
import heroImage from "../assets/images/hero.png";
import notFoundMissedVanImage from "../assets/images/not_found_missed_van2_resized.png";
import Devider from "../ThemeComponents/Devider";
import LocationLogoHeader from "../ThemeComponents/LocationPage/LocationLogoHeader";
import PriceVatToggle from "../ThemeComponents/PriceVatToggle";
import {getPriceVatPreference, setPriceVatPreference} from "../config/priceVatPreference";
import {appendPublicWrapperBaseToSearch} from "../config/publicWrapperBase";
import {sendEvent} from "../config/analytics";

const DEMO_LOCATION_SHOW_ALL_TYPES_ID = '875'
const PASSENGER_VEHICLE_TYPES = new Set(['personenauto'])
const CARGO_VEHICLE_TYPES = new Set(['bestelbus', 'vrachtwagen'])

const AUTOMATIC_TRANSMISSION = 'automatic'

const slugify = (value = '') =>
    String(value)
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')

const isAutomaticTransmission = (vehicle) =>
    String(vehicle?.model?.transmission || '')
        .trim()
        .toLowerCase() === AUTOMATIC_TRANSMISSION

const getVehicleGroupPriority = (vehicleType = '') => {
    const normalizedType = String(vehicleType || '').trim().toLowerCase()
    if (PASSENGER_VEHICLE_TYPES.has(normalizedType)) return 0
    if (CARGO_VEHICLE_TYPES.has(normalizedType)) return 1
    return 2
}

const getVehicleSortPrice = (vehicle, showPricesInclVat = true) => {
    const categoryOptions = Array.isArray(vehicle?.category_options) ? vehicle.category_options : []
    if (categoryOptions.length > 0) {
        const priceKey = showPricesInclVat ? 'price_including_vat' : 'price_excluding_vat'
        const minCategoryPrice = categoryOptions.reduce((minPrice, option) => {
            const nextPrice = Number(option?.[priceKey])
            if (!Number.isFinite(nextPrice)) return minPrice
            return Math.min(minPrice, nextPrice)
        }, Number.POSITIVE_INFINITY)
        if (Number.isFinite(minCategoryPrice)) return minCategoryPrice
    }

    const fallbackPrices = [
        Number(vehicle?.prices?.day),
        Number(vehicle?.prices?.hour),
        Number(vehicle?.prices?.weekend),
        Number(vehicle?.prices?.week)
    ]
    const firstFiniteFallbackPrice = fallbackPrices.find((price) => Number.isFinite(price))
    if (Number.isFinite(firstFiniteFallbackPrice)) return firstFiniteFallbackPrice
    return Number.POSITIVE_INFINITY
}

const LocationPage = () => {
    const {id, vehicleType} = useParams();
    const location = useLocation()
    const navigate = useNavigate()
    const isDemoLocationShowAllTypes = String(id) === DEMO_LOCATION_SHOW_ALL_TYPES_ID
    const autoSelectHandledRef = useRef(false)
    const noVehiclesEventSentRef = useRef(false)

    const [locationInfo, setLocationInfo] = useState(null)
    const [vehicleList, setVehiclesList] = useState([])
    const [showPricesInclVat, setShowPricesInclVat] = useState(() => getPriceVatPreference(true))
    const [selectedFilters, setSelectedFilters] = useState({
        type: new Set(),
        transmission: new Set(),
        fuel: new Set(),
        seats: new Set(),
        doors: new Set()
    })

    const filterOptions = useMemo(() => {
        const typeSet = new Set()
        const transmissionSet = new Set()
        const fuelSet = new Set()
        const seatsSet = new Set()
        const doorsSet = new Set()

        vehicleList.forEach((vehicle) => {
            const model = vehicle.model || {}
            if (model.vehicleType) typeSet.add(model.vehicleType)
            if (model.transmission) transmissionSet.add(model.transmission)
            if (model.fuelType) fuelSet.add(model.fuelType)
            if (Number.isFinite(model.numberOfSeats)) seatsSet.add(model.numberOfSeats)
            if (Number.isFinite(model.numberOfDoors)) doorsSet.add(model.numberOfDoors)
        })

        const formatLabel = (value = '') =>
            String(value)
                .replace(/_/g, ' ')
                .replace(/\b\w/g, (char) => char.toUpperCase())

        const typeLabel = (value) => {
            const map = {
                bestelbus: 'Bestelbus',
                personenauto: 'Personenauto',
                personenbus: 'Personenbus',
                vrachtwagen: 'Vrachtwagen'
            }
            return map[value] || formatLabel(value)
        }

        const transmissionLabel = (value) => {
            const map = {
                automatic: 'Automaat',
                manual: 'Handgeschakeld'
            }
            return map[value] || formatLabel(value)
        }

        const fuelLabel = (value) => {
            const map = {
                diesel: 'Diesel',
                euro95: 'Benzine',
                electric: 'Elektrisch',
                electricity: 'Elektrisch',
                hybrid: 'Hybride'
            }
            return map[value] || formatLabel(value)
        }

        const toOptions = (set, labelFn, sorter) =>
            Array.from(set)
                .sort(sorter)
                .map((value) => ({
                    value,
                    label: labelFn(value)
                }))

        return {
            type: toOptions(typeSet, typeLabel, (a, b) => String(a).localeCompare(String(b))),
            transmission: toOptions(
                transmissionSet,
                transmissionLabel,
                (a, b) => String(a).localeCompare(String(b))
            ),
            fuel: toOptions(fuelSet, fuelLabel, (a, b) => String(a).localeCompare(String(b))),
            seats: toOptions(seatsSet, (value) => `${value} personen`, (a, b) => a - b),
            doors: toOptions(doorsSet, (value) => `${value} deuren`, (a, b) => a - b)
        }
    }, [vehicleList])

    useEffect(() => {
        setSelectedFilters((prev) => {
            const next = {
                type: new Set(prev.type),
                transmission: new Set(prev.transmission),
                fuel: new Set(prev.fuel),
                seats: new Set(prev.seats),
                doors: new Set(prev.doors)
            }

            const available = {
                type: new Set(filterOptions.type.map((item) => item.value)),
                transmission: new Set(filterOptions.transmission.map((item) => item.value)),
                fuel: new Set(filterOptions.fuel.map((item) => item.value)),
                seats: new Set(filterOptions.seats.map((item) => item.value)),
                doors: new Set(filterOptions.doors.map((item) => item.value))
            }

            next.type = new Set([...next.type].filter((value) => available.type.has(value)))
            next.transmission = new Set(
                [...next.transmission].filter((value) => available.transmission.has(value))
            )
            next.fuel = new Set([...next.fuel].filter((value) => available.fuel.has(value)))
            next.seats = new Set([...next.seats].filter((value) => available.seats.has(value)))
            next.doors = new Set([...next.doors].filter((value) => available.doors.has(value)))

            if (isDemoLocationShowAllTypes) {
                next.type = new Set(available.type)
            } else if (vehicleType && available.type.has(vehicleType) && next.type.size === 0) {
                next.type.add(vehicleType)
            }

            return next
        })
    }, [filterOptions, vehicleType, isDemoLocationShowAllTypes])

    const toggleFilter = (group, value) => {
        setSelectedFilters((prev) => {
            const next = {
                type: new Set(prev.type),
                transmission: new Set(prev.transmission),
                fuel: new Set(prev.fuel),
                seats: new Set(prev.seats),
                doors: new Set(prev.doors)
            }

            const currentSet = next[group]
            if (!currentSet) return prev
            if (currentSet.has(value)) {
                currentSet.delete(value)
            } else {
                currentSet.add(value)
            }
            return next
        })
    }

    const filteredVehicles = useMemo(() => {
        const visibleVehicles = vehicleList.filter((vehicle) => {
            const model = vehicle.model || {}
            if (selectedFilters.type.size && !selectedFilters.type.has(model.vehicleType)) return false
            if (
                selectedFilters.transmission.size &&
                !selectedFilters.transmission.has(model.transmission)
            ) {
                return false
            }
            if (selectedFilters.fuel.size && !selectedFilters.fuel.has(model.fuelType)) return false
            if (
                selectedFilters.seats.size &&
                !selectedFilters.seats.has(model.numberOfSeats)
            ) {
                return false
            }
            if (
                selectedFilters.doors.size &&
                !selectedFilters.doors.has(model.numberOfDoors)
            ) {
                return false
            }
            return true
        })

        return visibleVehicles.slice().sort((left, right) => {
            const leftType = left?.model?.vehicleType
            const rightType = right?.model?.vehicleType
            const leftGroupPriority = getVehicleGroupPriority(leftType)
            const rightGroupPriority = getVehicleGroupPriority(rightType)

            if (leftGroupPriority !== rightGroupPriority) {
                return leftGroupPriority - rightGroupPriority
            }

            const leftPrice = getVehicleSortPrice(left, showPricesInclVat)
            const rightPrice = getVehicleSortPrice(right, showPricesInclVat)
            if (leftPrice !== rightPrice) {
                return leftPrice - rightPrice
            }

            const leftName = String(left?.name || '')
            const rightName = String(right?.name || '')
            const byName = leftName.localeCompare(rightName, 'nl')
            if (byName !== 0) {
                return byName
            }

            const leftId = Number(left?.id)
            const rightId = Number(right?.id)
            if (Number.isFinite(leftId) && Number.isFinite(rightId)) {
                return leftId - rightId
            }

            return 0
        })
    }, [vehicleList, selectedFilters, showPricesInclVat])

    useEffect(() => {
        if (id) {
            (async () => {
                try {
                    const response = await getLocationInfo({locationId: id, force: DISABLED_CACHE})
                    const vehiclesResponse = await searchVehiclesAPI({locationId: id, force: DISABLED_CACHE})
                    setLocationInfo(response)
                    // Equivalent vehicles share one card; the reservation step swaps to a free one.
                    setVehiclesList(collapseEquivalentVehicles(vehiclesResponse))
                    window.scrollTo({
                        top: 0,
                        left: 0,
                        behavior: "smooth" // можно убрать, если без анимации
                    });
                } catch (error) {
                    console.log(error)
                }
            })()
        }
    }, [id]);

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search)
        const shouldAutoSelect = String(queryParams.get('autoselect') || '').toLowerCase() === 'true'

        if (!shouldAutoSelect || vehicleList.length === 0 || autoSelectHandledRef.current) return

        // Prefer automatic transmission over manual:
        // if automatic vehicles exist, pick randomly among them only,
        // otherwise fall back to a random pick from the whole list.
        const automaticVehicles = vehicleList.filter(isAutomaticTransmission)
        const candidateVehicles = automaticVehicles.length > 0 ? automaticVehicles : vehicleList

        const selectedVehicle = candidateVehicles[Math.floor(Math.random() * candidateVehicles.length)]
        if (!selectedVehicle?.id) return

        autoSelectHandledRef.current = true
        const reservationParams = new URLSearchParams()
        const expectedKm = queryParams.get('expectedKm') || queryParams.get('expectedKM')
        if (expectedKm) reservationParams.set('expectedKm', expectedKm)
        ;['from', 'to'].forEach((param) => {
            const value = queryParams.get(param)
            if (value) reservationParams.set(param, value)
        })

        navigate({
            pathname: `/reservation/${selectedVehicle.id}/${selectedVehicle.location?.id || id}`,
            search: appendPublicWrapperBaseToSearch(reservationParams, location.search).toString(),
        })
    }, [id, location.search, navigate, vehicleList])

    useEffect(() => {
        autoSelectHandledRef.current = false
        noVehiclesEventSentRef.current = false
    }, [id])

    const locationSupportRemoteRental = useMemo(() => {
        return vehicleList.find(({communication_type}) => communication_type === 'invers_cloudboxx')
    }, [vehicleList])

    const isHourlyRentalLocation = useMemo(() => {
        return vehicleList.some((vehicle) => {
            const unit = String(vehicle?.rental_config?.minimum_rental_time?.unit || "").trim().toLowerCase()
            return unit === "hour"
        })
    }, [vehicleList])

    const handlePriceVatPreferenceChange = (nextShowPricesInclVat) => {
        setShowPricesInclVat(nextShowPricesInclVat)
        setPriceVatPreference(nextShowPricesInclVat)
    }

    const hasAvailableVehicles = filteredVehicles.length > 0

    useEffect(() => {
        if (!locationInfo) return
        if (hasAvailableVehicles) return
        if (noVehiclesEventSentRef.current) return
        noVehiclesEventSentRef.current = true
        sendEvent("reserve_no_vehicles")
    }, [locationInfo, hasAvailableVehicles])

    const citySlug = slugify(locationInfo?.address?.city)
    const pickupPointsUrl = citySlug
        ? `https://kav2go.nl/busje-huren-${citySlug}/#locations`
        : null

    return (
        <>
            <Header/>
            <Devider />

            {
                locationInfo ?
                    <>
                        {
                            locationInfo.isFake ?
                                <FakeLocationPage locationInfo={locationInfo} />
                                :
                                <>
                                    <LocationLogoHeader locationInfo={locationInfo} vehicleList={vehicleList} />

                                    {
                                        isDemoLocationShowAllTypes ?
                                            <section className="front-demo-location-hero">
                                                <div
                                                    className="front-demo-location-hero__bg"
                                                    style={{backgroundImage: `url(${heroImage})`}}
                                                >
                                                    <div className="front-demo-location-hero__surface">
                                                        <div className="container">
                                                        <div className="front-demo-location-hero__overlay">
                                                            <p className="front-demo-location-hero__welcome">
                                                                {`Welkom bij ${isDemoLocationShowAllTypes ? 'Demo Car Rent' : (locationInfo?.company?.name || 'KAV2GO')}. U gaat een voertuig huren.`}
                                                            </p>
                                                            <p className="front-demo-location-hero__title">
                                                                Laten we beginnen!
                                                            </p>
                                                            <div className="front-demo-location-hero__step">
                                                                <span className="front-demo-location-hero__stepBadge">Stap&nbsp;1</span>
                                                                <p>
                                                                    Kies een van de onderstaande voertuigen
                                                                    <br/>
                                                                    en klik op ‘direct huren’.
                                                                </p>
                                                            </div>
                                                        </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </section>
                                            :
                                            null
                                    }

                                    {hasAvailableVehicles ? (
                                        <>
                                            <div className={isDemoLocationShowAllTypes ? "front-demo-location-catalog" : ""}>
                                                <div className="container">
                                                    <div className="row">
                                                        <div className="col-12">
                                                            <PriceVatToggle
                                                                showPricesInclVat={showPricesInclVat}
                                                                onChange={handlePriceVatPreferenceChange}
                                                            />
                                                            <Filter
                                                                filterOptions={filterOptions}
                                                                selectedFilters={selectedFilters}
                                                                onToggle={toggleFilter}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="front-location-background" />
                                                <div className="container mb-5">
                                                    <div className="row">
                                                        {filteredVehicles.map((vehicle) => (
                                                            <div className="col-lg-3 col-md-6 col-sm-6 mt-5"
                                                                 key={vehicle.id}>
                                                                <VehicleComponent vehicle={vehicle} showPricesInclVat={showPricesInclVat}/>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                            {!isHourlyRentalLocation ? (
                                                <div className="container mb-5">
                                                    <div className="row">
                                                        <div className="col-12">
                                                            <LocationSchedule location={locationInfo}
                                                                              isRemoteRental={locationSupportRemoteRental}/>
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : null}
                                            <div className="container mb-5">
                                                <div className="row">
                                                    <div className="col-12">
                                                        <LocationSeo location={locationInfo}/>
                                                    </div>
                                                </div>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="container mb-5">
                                            <div className="front-location-empty-state">
                                                <img
                                                    className="front-location-empty-state__image"
                                                    src={notFoundMissedVanImage}
                                                    alt=""
                                                />
                                                <div className="front-location-empty-state__content">
                                                    <h2>Helaas,</h2>
                                                    <p>
                                                        Dit voertuig is tijdelijk niet beschikbaar. Kies een Pick up Point in de buurt.
                                                    </p>
                                                    {pickupPointsUrl ? (
                                                        <a href={pickupPointsUrl}>Klik hier om beschikbare busjes te reserveren</a>
                                                    ) : (
                                                        <span>Klik hier om beschikbare busjes te reserveren</span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </>
                        }
                    </>
                    :
                    <LocationPageLoader/>
            }

            <Footer/>
        </>
    )

}

export default LocationPage
